import type { ContactGroup } from './ContactGroupTypes'
export type { ContactGroup, ContactGroupMember } from './ContactGroupTypes'
import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DetailPage } from '@/components/templates/detail-page'
import { ListPage, type ListPageColumn } from '@/components/templates/list-page'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { deleteJSON, getJSON, patchJSON, postJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import type { Contact } from '@/lib/contacts/types'
import { GroupDetailActions, GroupMembersTable } from '@/pages/ContactGroupMembers'
import type { Segment } from '@/pages/SegmentBuilderPage'

interface GroupsResponse {
  groups: ContactGroup[]
}

interface GroupResponse {
  group: ContactGroup
}

interface SegmentsResponse {
  segments: Segment[]
}

interface ContactsResponse {
  contacts: Contact[]
}

type Audience =
  | { kind: 'group'; id: string; name: string; count: number; group: ContactGroup }
  | { kind: 'segment'; id: string; name: string; count: number; segment: Segment }

const audienceColumns: ListPageColumn<Audience>[] = [
  {
    id: 'name',
    header: 'Name',
    cell: (audience) => <span className="font-medium text-foreground">{audience.name}</span>,
  },
  {
    id: 'kind',
    header: 'Type',
    cell: (audience) => (
      <Badge variant="neutral">{audience.kind === 'group' ? 'Group' : 'Segment'}</Badge>
    ),
  },
  {
    id: 'members',
    header: 'Matches',
    headerClassName: 'text-right',
    className: 'text-right tabular-nums',
    cell: (audience) =>
      audience.kind === 'group' ? (
        <div className="flex flex-col items-end text-xs">
          <span>Memberships: {audience.group.membership_count ?? audience.count}</span>
          <span>
            People with member status:{' '}
            {audience.group.people_with_member_status_count ?? audience.count}
          </span>
        </div>
      ) : (
        audience.count
      ),
  },
]

export function ContactGroupsPage({
  groupManage = false,
  onOpen,
  onOpenSegment,
  onCreateSegment,
}: {
  /** The API's `groups` manage capability; without it the list offers no Create Group control. */
  groupManage?: boolean
  onOpen?: (group: ContactGroup) => void
  onOpenSegment?: (segment: Segment) => void
  onCreateSegment?: () => void
}) {
  const [creating, setCreating] = React.useState(false)
  const groupsQuery = useQuery({
    queryKey: ['contact-groups'],
    queryFn: () => getJSON<GroupsResponse>('/api/v1/groups'),
    retry: false,
  })
  const segmentsQuery = useQuery({
    queryKey: ['segments'],
    queryFn: () => getJSON<SegmentsResponse>('/api/v1/segments'),
    retry: false,
  })
  const audiences: Audience[] = [
    ...(groupsQuery.data?.groups ?? []).map((group) => ({
      kind: 'group' as const,
      id: `group-${group.id}`,
      name: group.name,
      count: group.member_count,
      group,
    })),
    ...(segmentsQuery.data?.segments ?? []).map((segment) => ({
      kind: 'segment' as const,
      id: `segment-${segment.id}`,
      name: segment.name,
      count: segment.match_count,
      segment,
    })),
  ].sort((left, right) => left.name.localeCompare(right.name))
  const loading = groupsQuery.isLoading || segmentsQuery.isLoading
  const error = groupsQuery.isError || segmentsQuery.isError

  return (
    <>
      <ListPage
        title="Segments & Groups"
        primaryAction={{ label: 'Create Segment', onClick: onCreateSegment }}
        toolbar={
          groupManage ? (
            <Button
              variant="outline"
              className="h-auto border-dashed px-1.5 py-0.5 text-micro"
              onClick={() => setCreating(true)}
            >
              Create Group
            </Button>
          ) : undefined
        }
        table={{
          rows: audiences,
          columns: audienceColumns,
          getRowId: (audience) => audience.id,
          getRowLabel: (audience) => audience.name,
          onRowOpen: (audience) =>
            audience.kind === 'group'
              ? onOpen?.(audience.group)
              : onOpenSegment?.(audience.segment),
          state: loading ? 'loading' : error ? 'error' : 'ready',
          loadingTitle: 'Loading Segments & Groups',
          emptyTitle: 'No Segments or Groups yet',
          errorTitle: 'Segments & Groups unavailable',
        }}
      />
      <GroupNameDialog open={creating} onOpenChange={setCreating} />
    </>
  )
}

function GroupNameDialog({
  open,
  onOpenChange,
  group,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  group?: ContactGroup
}) {
  const queryClient = useQueryClient()
  const [name, setName] = React.useState(group?.name ?? '')
  React.useEffect(() => setName(group?.name ?? ''), [group, open])
  const save = useMutation({
    mutationFn: () =>
      group
        ? patchJSON<GroupResponse>(`/api/v1/groups/${group.id}`, { group: { name } })
        : postJSON<GroupResponse>('/api/v1/groups', { group: { name } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['contact-groups'] })
      if (group) await queryClient.invalidateQueries({ queryKey: ['contact-group', group.id] })
      onOpenChange(false)
      setName('')
    },
  })
  const nameError = apiFieldMessages(save.error, 'name')[0]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{group ? 'Rename Group' : 'Create Group'}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            save.mutate()
          }}
        >
          {save.isError && !nameError && <Alert>Group could not be saved.</Alert>}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="group-name">Group name</Label>
            <Input
              id="group-name"
              value={name}
              aria-invalid={Boolean(nameError)}
              onChange={(event) => setName(event.target.value)}
            />
            {nameError && <p className="text-caption text-destructive">{nameError}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save Group'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function ContactGroupDetailPage({
  groupId,
  onBack,
  onDeleted,
}: {
  groupId: number
  onBack: () => void
  onDeleted?: () => void
}) {
  const queryClient = useQueryClient()
  const [adding, setAdding] = React.useState(false)
  const [renaming, setRenaming] = React.useState(false)
  const [confirmingDelete, setConfirmingDelete] = React.useState(false)
  const groupQuery = useQuery({
    queryKey: ['contact-group', groupId],
    queryFn: () => getJSON<GroupResponse>(`/api/v1/groups/${groupId}`),
    retry: false,
  })
  const removeMember = useMutation({
    mutationFn: (contactId: number) => deleteJSON(`/api/v1/groups/${groupId}/members/${contactId}`),
    onSuccess: async () => {
      await invalidateGroupQueries(queryClient, groupId)
    },
  })
  const deleteGroup = useMutation({
    mutationFn: () => deleteJSON(`/api/v1/groups/${groupId}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['contact-groups'] })
      onDeleted?.()
    },
  })

  if (groupQuery.isLoading) {
    return <p className="p-6 text-body-sm text-muted-foreground">Loading Group…</p>
  }
  if (groupQuery.isError || !groupQuery.data) {
    return <Alert className="m-6">Group unavailable</Alert>
  }

  const group = groupQuery.data.group
  return (
    <DetailPage
      title={group.name}
      subtitle={`${group.member_count} ${group.member_count === 1 ? 'member' : 'members'}`}
      badge={<Badge variant="neutral">Group</Badge>}
      backHref="/communication/audiences"
      backLabel="Segments & Groups"
      onBack={onBack}
      actions={
        <GroupDetailActions
          onAdd={() => setAdding(true)}
          onRename={() => setRenaming(true)}
          onDelete={() => setConfirmingDelete(true)}
        />
      }
    >
      {removeMember.isError && <Alert className="mb-4">Contact could not be removed.</Alert>}
      <GroupMembersTable members={group.members} onRemove={(id) => removeMember.mutate(id)} />
      <AddContactsDialog group={group} open={adding} onOpenChange={setAdding} />
      <GroupNameDialog group={group} open={renaming} onOpenChange={setRenaming} />
      <ConfirmDeleteDialog
        open={confirmingDelete}
        onOpenChange={setConfirmingDelete}
        groupName={group.name}
        pending={deleteGroup.isPending}
        onConfirm={() => deleteGroup.mutate()}
      />
    </DetailPage>
  )
}

function AddContactsDialog({
  group,
  open,
  onOpenChange,
}: {
  group: ContactGroup
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const contactsQuery = useQuery({
    queryKey: ['contacts', 'group-picker'],
    queryFn: () => getJSON<ContactsResponse>('/api/v1/contacts?include_archived=true'),
    enabled: open,
    retry: false,
  })
  const addMember = useMutation({
    mutationFn: (contactId: number) =>
      postJSON<GroupResponse>(`/api/v1/groups/${group.id}/members`, {
        member: { contact_id: contactId },
      }),
    onSuccess: async () => {
      await invalidateGroupQueries(queryClient, group.id)
      onOpenChange(false)
    },
  })
  const memberIds = new Set(group.members.map((member) => member.id))
  const available = (contactsQuery.data?.contacts ?? []).filter(
    (contact) => !memberIds.has(contact.id),
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Contacts</DialogTitle>
        </DialogHeader>
        {contactsQuery.isLoading ? (
          <p className="text-body-sm text-muted-foreground">Loading Contacts…</p>
        ) : contactsQuery.isError ? (
          <Alert>Contacts unavailable</Alert>
        ) : available.length === 0 ? (
          <p className="text-body-sm text-muted-foreground">
            Every Contact is already in this Group.
          </p>
        ) : (
          <div className="divide-y divide-border rounded-md border border-border">
            {available.map((contact) => (
              <div key={contact.id} className="flex items-center justify-between gap-3 px-3 py-2">
                <div>
                  <p className="text-body-sm font-medium text-foreground">{contact.name}</p>
                  <p className="text-caption text-muted-foreground">
                    {contact.contact_type}
                    {contact.archived ? ' · Archived' : ''}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-auto border-dashed px-1.5 py-0.5 text-micro"
                  aria-label={`Add ${contact.name}`}
                  disabled={addMember.isPending}
                  onClick={() => addMember.mutate(contact.id)}
                >
                  Add
                </Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function ConfirmDeleteDialog({
  open,
  onOpenChange,
  groupName,
  pending,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  groupName: string
  pending: boolean
  onConfirm: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Group?</DialogTitle>
          <DialogDescription>
            {groupName} will be deleted. Its Contacts will not be deleted.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" disabled={pending} onClick={onConfirm}>
            {pending ? 'Deleting…' : 'Delete Group'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

async function invalidateGroupQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  groupId: number,
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['contact-groups'] }),
    queryClient.invalidateQueries({ queryKey: ['contact-group', groupId] }),
  ])
}
